/**
 * BugReporter — small header button + modal for user bug reports.
 * Optional screenshot upload or capture via html2canvas.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { db, storage } from '../firebase';
import { addDoc, collection, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import { withTenant } from '../services/data';
import { useTenant } from '../contexts/TenantContext';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { notifyAdmins } from '../lib/notifications';
import { useFabClearance, useQuickActionsReplaced } from '../hooks/useFabClearance';
import { useOpenModalCount, ModalOpenMarker } from '../hooks/useModalOpen';
import { BUG_FAB, bugLauncherSpot } from '../lib/fabStack';

const SEVERITY_OPTIONS = [
  { k: 'minor', l: 'Minor' },
  { k: 'major', l: 'Major' },
  { k: 'critical', l: 'Critical' },
];

// Words longer than 3 chars, lowercased, deduped
function tokenize(text) {
  return new Set(
    String(text || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length > 3)
  );
}

// Word-overlap similarity: shared / max(setA, setB)
function wordOverlap(a, b) {
  if (a.size === 0 || b.size === 0) return 0;
  let shared = 0;
  a.forEach((w) => { if (b.has(w)) shared += 1; });
  return shared / Math.max(a.size, b.size);
}

/** Interactive things inside an open modal the launcher must not sit on. */
const MODAL_BUTTONS = ['button', 'a[href]', '[role="button"]', 'input[type="submit"]', 'input[type="button"]']
  .map((sel) => `[aria-modal="true"] ${sel}`).join(', ');

const sameRects = (a, b) => a.length === b.length
  && a.every((r, i) => r.left === b[i].left && r.top === b[i].top && r.right === b[i].right && r.bottom === b[i].bottom);

/**
 * RW-2953: the viewport rects of every button in an open modal, kept live
 * (resize, scroll inside the modal, the panel changing size) while any modal
 * is open. Keyed on the open COUNT, so a second modal opening over the first
 * is measured too.
 */
function useModalButtonRects(openCount) {
  const active = openCount > 0;
  const [rects, setRects] = useState([]);
  useEffect(() => {
    if (!active) { setRects((prev) => (prev.length ? [] : prev)); return undefined; }
    let raf = 0;
    const measure = () => {
      raf = 0;
      const next = [];
      document.querySelectorAll(MODAL_BUTTONS).forEach((n) => {
        const r = n.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) next.push({ left: r.left, right: r.right, top: r.top, bottom: r.bottom });
      });
      setRects((prev) => (sameRects(prev, next) ? prev : next));
    };
    const schedule = () => { if (!raf) raf = window.requestAnimationFrame(measure); };
    measure();
    schedule(); // once more after the modal's first paint settles
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(schedule);
      document.querySelectorAll('[aria-modal="true"]').forEach((m) => {
        ro.observe(m);
        Array.from(m.children).forEach((c) => ro.observe(c));
      });
    }
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      if (ro) ro.disconnect();
    };
  }, [active, openCount]);
  return rects;
}

export default function BugReporter({ user, mobile = false }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  // RW-1486: lift above bottom-pinned bars (Ask Rêve composer) in step with
  // the QuickActionsFab so the stack keeps its 66px spacing.
  const fabLift = useFabClearance();
  // RW-2953 (Clint): the launcher "should sit above any modal but not over
  // another button". It keeps its max z-index — a bug inside a modal must
  // still be reportable — and moves off any button of the open modal. Quick
  // actions steps aside while a modal is up (or the phone feed's own "+"
  // replaces it), and the launcher drops into that slot.
  const openModals = useOpenModalCount();
  const modalOpen = openModals > 0;
  const quickReplaced = useQuickActionsReplaced();
  const avoid = useModalButtonRects(openModals);
  // RW-176: Global keyboard shortcut Cmd/Ctrl+Shift+B opens the bug reporter
  // from anywhere, regardless of which modal/view is active.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'B' || e.key === 'b')) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  if (!user) return null;
  const spot = bugLauncherSpot({
    width: window.innerWidth,
    height: window.innerHeight,
    mobile,
    lift: fabLift,
    quickShown: !modalOpen && !quickReplaced,
    avoid,
  });
  // RW-286 + RW-1022 + RW-414 (part 1): a fixed-position floating widget at max z-index,
  // PORTALED to document.body. Max z-index alone was NOT enough — BugReporter mounts inside
  // the app shell (App.js), whose `transform` animation creates a stacking context that traps
  // the button's z-index locally, so body-level modal backdrops (z-index up to ~100000) still
  // covered it and ate the click (repeatedly reported: RW-1022 / RW-414 / RW-404 / RW-197 …).
  // Portaling to document.body lifts the button + modal out of that trapped context so
  // they truly sit above everything. The launcher button uses the max 32-bit z-index
  // (2147483647) with pointerEvents:'auto', so it stays clickable even while any modal/popup
  // overlay is open — that is RW-414 part 1. React context (Router/Tenant) still flows through
  // the portal, so submission/tenant scoping is unaffected.
  return createPortal(
    <>
      <button
        onClick={() => setOpen(true)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        // RW-655 (D6-A2-4): surfaced as the in-platform help path, not just bugs
        aria-label="Get help or report a bug (⌘⇧B)"
        title="Get help or report a bug (⌘⇧B)"
        data-html2canvas-ignore="true"
        style={{
          position: 'fixed',
          // RW-2269: offsets from src/lib/fabStack.js. On the feed the create-post
          // FAB registers itself as an obstruction, so this lifts above it
          // instead of sitting inside its 56px box and eating the tap.
          // RW-2953: bugLauncherSpot also moves it off an open modal's buttons.
          bottom: spot.bottom,
          // Center-align with the QuickActionsFab (56×56 at right:24, center at 52px).
          // Bug button is 40px wide, so right:32 puts its center at 32+20 = 52.
          [spot.side]: spot.offset,
          width: BUG_FAB.size,
          height: BUG_FAB.size,
          borderRadius: '50%',
          background: hover ? '#3A78B8' : '#4A90D9',
          color: '#fff',
          border: 'none',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18,
          boxShadow: hover ? '0 6px 18px rgba(74,144,217,0.45)' : '0 4px 12px rgba(0,0,0,0.18)',
          transition: 'background 150ms, box-shadow 150ms, bottom 200ms ease',
          zIndex: 2147483647,
          pointerEvents: 'auto',
        }}>
        🐛
      </button>
      {open ? <BugReportModal user={user} onClose={() => setOpen(false)} /> : null}
    </>,
    document.body
  );
}

function BugReportModal({ user, onClose }) {
  const location = useLocation();
  const { tenantId } = useTenant();
  const [type, setType] = useState(null); // 'bug' | 'improvement'
  const [typeError, setTypeError] = useState('');
  const [description, setDescription] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [severity, setSeverity] = useState('major');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [duplicates, setDuplicates] = useState([]);
  const [checkingDupes, setCheckingDupes] = useState(false);
  // RW-197: explicit submitted flag so the success panel shows long enough
  // for users to see confirmation instead of the modal silently disappearing.
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);
  const dupeTimerRef = useRef(null);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setToast('Max file size is 5MB'); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  // Debounced duplicate detection — runs 500ms after typing stops on 10+ char descriptions
  useEffect(() => {
    if (dupeTimerRef.current) clearTimeout(dupeTimerRef.current);
    if (!tenantId) return undefined;
    if (description.trim().length < 10) {
      setDuplicates([]);
      return undefined;
    }
    dupeTimerRef.current = setTimeout(async () => {
      setCheckingDupes(true);
      try {
        // Pull all reports whose status is in the open set; status==='new' || 'investigating' is "open"
        // We avoid Firestore 'not-in' (limited to 10) by querying both open statuses with 'in'.
        // RW-707: tenantId filter required by query-shape rule (RW-684 pattern)
        const snap = await getDocs(query(
          collection(db, 'bugReports'),
          where('tenantId', '==', tenantId),
          where('status', 'in', ['new', 'investigating']),
        ));
        const myTokens = tokenize(description);
        if (myTokens.size === 0) { setDuplicates([]); setCheckingDupes(false); return; }
        const currentPath = location.pathname;
        const scored = [];
        snap.forEach((d) => {
          const data = d.data() || {};
          const theirTokens = tokenize(data.description || '');
          let score = wordOverlap(myTokens, theirTokens);
          // Boost confidence if same page
          if (data.page && data.page === currentPath) score += 0.15;
          if (score > 0.4) scored.push({ id: d.id, score, ...data });
        });
        scored.sort((a, b) => b.score - a.score);
        setDuplicates(scored.slice(0, 2));
      } catch (e) {
        console.warn('duplicate check failed', e);
        setDuplicates([]);
      } finally {
        setCheckingDupes(false);
      }
    }, 500);
    return () => { if (dupeTimerRef.current) clearTimeout(dupeTimerRef.current); };
  }, [description, location.pathname, tenantId]);

  const captureScreenshot = useCallback(async () => {
    setCapturing(true);
    const modal = document.getElementById('reve-bug-modal');
    if (modal) modal.style.visibility = 'hidden';
    let blob = null;
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        logging: false,
        scale: 1,
        // Opaque base so the PNG isn't exported with alpha (transparent regions
        // read as a washed-out / faded capture). Match the app's warm canvas bg.
        backgroundColor: '#F0EEE9',
        // Give lazy/below-the-fold images time to load into the clone.
        imageTimeout: 15000,
        ignoreElements: (element) => {
          if (!element || !element.tagName) return false;
          if (element.tagName === 'IFRAME') return true;
          if (element.classList?.contains?.('bug-reporter-modal')) return true;
          if (element.getAttribute?.('data-html2canvas-ignore') === 'true') return true;
          return false;
        },
        onclone: (clonedDoc) => {
          // The capture was coming out faded: elements mid entrance-animation /
          // opacity-transition, and `loading="lazy"` images not yet loaded, get
          // snapshotted half-rendered. Neutralize that in the clone (only affects
          // the throwaway capture DOM, never the live page).
          try {
            const s = clonedDoc.createElement('style');
            s.textContent = '*,*::before,*::after{animation:none!important;transition:none!important;opacity:1!important;}';
            (clonedDoc.head || clonedDoc.documentElement).appendChild(s);
            clonedDoc.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
          } catch (_) { /* never let cleanup crash the capture */ }
        },
      });
      blob = await new Promise((resolve) => {
        try { canvas.toBlob((b) => resolve(b), 'image/png', 0.8); }
        catch { resolve(null); }
      });
    } catch (e) {
      // Swallow — never let html2canvas crash the app
      console.warn('Screenshot capture failed:', e?.message || e);
      setToast('Screenshot unavailable on this page');
    }
    if (modal) modal.style.visibility = 'visible';
    if (blob) {
      const f = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
      setFile(f);
      setPreview(URL.createObjectURL(blob));
    }
    setCapturing(false);
  }, []);

  const submit = useCallback(async () => {
    if (!type) { setTypeError('Please select whether this is a bug or an improvement'); return; }
    setTypeError('');
    if (!description.trim()) { setToast('Please describe the bug'); return; }
    setSaving(true);
    try {
      // Upload screenshot FIRST so the doc is created with the URL already set.
      // Previously the upload happened post-doc inside a swallowed try/catch,
      // so storage failures left reports without an attached image and no error.
      let screenshotUrl = null;
      let screenshotPath = null;
      if (file) {
        try {
          const path = `bugReports/${user?.uid || 'anon'}/${Date.now()}-${file.name}`;
          const sref = storageRef(storage, path);
          await uploadBytes(sref, file);
          screenshotUrl = await getDownloadURL(sref);
          screenshotPath = path;
        } catch (e) {
          console.error('screenshot upload failed', e);
          setToast('Screenshot upload failed: ' + (e?.message || 'unknown') + ' — submitting without it.');
        }
      }
      await addDoc(collection(db, 'bugReports'), withTenant('bugReports', {
        type,
        description: description.trim(),
        expectedBehavior: expectedBehavior.trim(),
        page: location.pathname,
        screenshotUrl,
        screenshotPath,
        severity,
        browser: navigator.userAgent || '',
        reportedBy: { uid: user?.uid || null, displayName: user?.displayName || '', email: user?.email || '' },
        status: 'new',
        createdAt: serverTimestamp(),
        resolution: null,
        resolvedAt: null,
        resolvedBy: null,
      }));
      // Fan out to admins (best-effort)
      notifyAdmins({
        type: 'bug_report',
        title: '🐛 New bug report',
        body: `${severity}: ${description.trim().substring(0, 60)}`,
        link: '/admin?section=bug-reports',
        sourceType: 'bugReport',
      }).catch(() => {});
      setToast(file && !screenshotUrl
        ? 'Bug reported (screenshot unavailable on this page)'
        : 'Bug report submitted — thank you!');
      setSubmitted(true);
      setTimeout(onClose, 2800);
    } catch (e) {
      console.error('bug report failed', e);
      setToast('Failed to submit: ' + (e?.message || 'unknown'));
    } finally { setSaving(false); }
  }, [type, description, expectedBehavior, severity, file, location.pathname, user, onClose]);

  // RW-176: keyboard flow — Escape closes, Cmd/Ctrl+Enter submits.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); submit(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submit]);

  if (submitted) {
    return (
      <div id="reve-bug-modal" data-html2canvas-ignore="true" className="bug-reporter-modal" role="dialog" aria-modal="true" aria-label="Report submitted" style={{ position: 'fixed', inset: 0, zIndex: 2147483000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <ModalOpenMarker />
        <div role="status" aria-live="polite" style={{ background: '#fff', borderRadius: 12, padding: '32px 28px', width: '92%', maxWidth: 420, fontFamily: "'Jost', sans-serif", textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: 28 }}>✓</div>
          <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 700, color: '#065F46' }}>Report submitted</h3>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: '#374151', lineHeight: 1.5 }}>Thanks — our team was just notified and a Linear ticket is being created.</p>
          <button onClick={onClose} style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: '#10B981', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div id="reve-bug-modal" data-html2canvas-ignore="true" className="bug-reporter-modal" role="dialog" aria-modal="true" aria-label="Report an issue" style={{ position: 'fixed', inset: 0, zIndex: 2147483000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {/* RW-2953: quick actions steps aside and the launcher moves off these buttons. */}
      <ModalOpenMarker />
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '92%', maxWidth: 540, maxHeight: '90vh', overflowY: 'auto', fontFamily: "'Jost', sans-serif" }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>Report an Issue</h3>
          <button aria-label="Close" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: '#9CA3AF', cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 14 }}>Page: <code style={{ background: '#F3F4F6', padding: '1px 6px', borderRadius: 3 }}>{location.pathname}</code></div>

        <div style={{ marginBottom: 14 }}>
          <label id="bug-type-label" style={labelSt}>What type of issue is this? <span style={{ color: '#EF4444' }}>*</span></label>
          <div role="group" aria-labelledby="bug-type-label" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { k: 'bug', emoji: '\uD83D\uDC1B', label: 'Bug', desc: 'Something is broken' },
              { k: 'improvement', emoji: '\uD83D\uDCA1', label: 'Improvement', desc: 'Works but could be better' },
            ].map((opt) => (
              <button key={opt.k} type="button" aria-pressed={type === opt.k} onClick={() => { setType(opt.k); setTypeError(''); }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  padding: '14px 10px', borderRadius: 10,
                  border: '2px solid', borderColor: type === opt.k ? '#6366F1' : '#E5E7EB',
                  background: type === opt.k ? '#EEF2FF' : '#fff',
                  cursor: 'pointer', minHeight: 80, transition: 'all 150ms',
                }}>
                <span style={{ fontSize: 24, marginBottom: 4 }}>{opt.emoji}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: type === opt.k ? '#4338CA' : '#111827' }}>{opt.label}</span>
                <span style={{ fontSize: 11, color: type === opt.k ? '#6366F1' : '#6B7280', marginTop: 2 }}>{opt.desc}</span>
              </button>
            ))}
          </div>
          {typeError ? <div style={{ color: '#EF4444', fontSize: 12, marginTop: 6 }}>{typeError}</div> : null}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label htmlFor="bug-description" style={labelSt}>What happened? <span style={{ color: '#EF4444' }}>*</span></label>
          <textarea id="bug-description" autoFocus value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what went wrong..." rows={4} style={textareaSt} />
          {duplicates.length > 0 ? (
            <div style={{ marginTop: 8, padding: 12, background: '#FEF3C7', border: '1px solid #FBBF24', borderRadius: 6 }}>
              <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#92400E' }}>
                ⚠️ Similar bug already reported{checkingDupes ? '…' : ''}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {duplicates.map((d) => {
                  const sevColor = d.severity === 'critical' ? '#DC2626' : d.severity === 'major' ? '#F59E0B' : '#6B7280';
                  const samePage = d.page && d.page === location.pathname;
                  return (
                    <div key={d.id} style={{ background: '#fff', border: '1px solid #FDE68A', borderRadius: 5, padding: 8, fontSize: 12, color: '#374151' }}>
                      <div style={{ fontWeight: 600, color: '#111827', marginBottom: 4, lineHeight: 1.4 }}>
                        {(d.description || '').slice(0, 120)}{(d.description || '').length > 120 ? '…' : ''}
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', fontSize: 10 }}>
                        <span style={{ padding: '2px 6px', borderRadius: 3, background: sevColor, color: '#fff', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {d.severity || 'minor'}
                        </span>
                        <span style={{ padding: '2px 6px', borderRadius: 3, background: '#F3F4F6', color: '#374151', fontWeight: 700, textTransform: 'uppercase' }}>
                          {(d.status || 'new').replace('-', ' ')}
                        </span>
                        <span style={{ color: '#6B7280', fontFamily: 'monospace' }}>{d.page || '—'}</span>
                        {samePage ? <span style={{ color: '#92400E', fontWeight: 700 }}>· same page</span> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p style={{ margin: '8px 0 0', fontSize: 11, color: '#92400E' }}>
                You can still <strong>Submit anyway</strong> using the button below.
              </p>
            </div>
          ) : null}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label htmlFor="bug-expected" style={labelSt}>What did you expect?</label>
          <textarea id="bug-expected" value={expectedBehavior} onChange={(e) => setExpectedBehavior(e.target.value)}
            placeholder="What should have happened instead?" rows={2} style={textareaSt} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label id="bug-severity-label" style={labelSt}>Severity</label>
          <div role="radiogroup" aria-labelledby="bug-severity-label" style={{ display: 'flex', gap: 8 }}>
            {SEVERITY_OPTIONS.map((opt) => (
              <label key={opt.k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 6, border: '1px solid', borderColor: severity === opt.k ? '#6366F1' : '#E5E7EB', background: severity === opt.k ? '#EEF2FF' : '#fff', color: severity === opt.k ? '#4338CA' : '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                <input type="radio" name="severity" value={opt.k} checked={severity === opt.k} onChange={() => setSeverity(opt.k)}
                  style={{ accentColor: '#6366F1', cursor: 'pointer' }} />
                {opt.l}
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label id="bug-screenshot-label" style={labelSt}>Screenshot (optional)</label>
          <div role="group" aria-labelledby="bug-screenshot-label" style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} style={{ display: 'none' }} />
            <button type="button" onClick={() => fileRef.current?.click()} style={secondaryBtn}>Upload image</button>
            <button type="button" onClick={captureScreenshot} disabled={capturing} style={{ ...secondaryBtn, opacity: capturing ? 0.5 : 1 }}>
              {capturing ? 'Capturing…' : '📸 Capture this page'}
            </button>
          </div>
          {preview ? (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img src={preview} alt="Screenshot preview" style={{ maxWidth: 200, maxHeight: 120, borderRadius: 6, border: '1px solid #E5E7EB', display: 'block' }} loading="lazy" />
              <button aria-label="Remove attachment" onClick={() => { setFile(null); setPreview(null); }} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', border: 'none', background: '#EF4444', color: '#fff', cursor: 'pointer', fontSize: 10 }}>✕</button>
            </div>
          ) : null}
        </div>

        {toast ? (
          <div role="alert" style={{
            padding: '10px 12px',
            background: toast.toLowerCase().includes('fail') ? '#FEF2F2' : '#EEF2FF',
            color: toast.toLowerCase().includes('fail') ? '#B91C1C' : '#4338CA',
            border: '1px solid',
            borderColor: toast.toLowerCase().includes('fail') ? '#FECACA' : '#C7D2FE',
            borderRadius: 6, fontSize: 12, marginBottom: 10, fontWeight: 600,
          }}>
            {toast}
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={secondaryBtn}>Cancel</button>
          <button onClick={submit} disabled={saving || !description.trim()}
            style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#6366F1', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saving || !description.trim() ? 0.5 : 1 }}>
            {saving ? 'Submitting…' : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>
  );
}

const labelSt = { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 };
const textareaSt = { width: '100%', padding: 10, borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: "'Jost', sans-serif" };
const secondaryBtn = { padding: '8px 14px', borderRadius: 6, border: '1px solid #E5E7EB', background: '#fff', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
